import { useEffect, useState, useCallback } from "react";
import {
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiX,
  FiCopy,
  FiUser,
  FiClock,
  FiEye,
} from "react-icons/fi";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { makeRequestAuth } from "../../../../../_config/api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../utils/toastUtils";

function AllInr() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [isConfirmingApprove, setIsConfirmingApprove] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  // Rejection Modal State
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchTransactions = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const response = await makeRequestAuth(
          "get",
          `/admin/getTransactionList?page=${page}&limit=10&keyWord=${searchTerm}&walletType=USDT&txType=deposit`,
        );

        if (response?.data) {
          const result = response.data;
          setTransactions(result.data || []);
          setTotal(result.total || 0);
          setTotalPages(result.pages || 1);
        }
      } catch (err) {
        showErrorToast(err.message || "Failed to fetch transactions");
      } finally {
        setIsLoading(false);
      }
    },
    [searchTerm],
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchTransactions(currentPage);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [currentPage, searchTerm, fetchTransactions]);

  const handleUpdateStatus = async (transactionId, status, reason = "") => {
    setIsUpdating(transactionId);
    try {
      const response = await makeRequestAuth(
        "post",
        "/admin/updateTransactionStatus",
        {
          transactionId,
          status,
          rejectReason: reason,
        },
      );

      console.log("response", response);

      if (!response.error) {
        showSuccessToast(`Transaction ${status}ed successfully`);
        setShowRejectModal(false);
        setRejectReason("");
        fetchTransactions(currentPage);
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update status");
    } finally {
      setIsUpdating(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccessToast("Copied to clipboard!");
  };

  const getStatusStyle = (status) => {
    const styles = {
      approve: "bg-emerald-100 text-emerald-700 border-emerald-200",
      pending: "bg-amber-100 text-amber-700 border-amber-200",
      reject: "bg-rose-100 text-rose-700 border-rose-200",
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  return (
    <div className="p-4 md:p-8 bg-gray-50 dark:bg-[#0f172a] min-h-screen font-sans">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Usdt Deposits
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Manage and verify incoming Usdt transactions.
            </p>
          </div>

          <div className="relative w-full md:w-96 group">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
            <input
              type="text"
              placeholder="Search Name, Email, or UPI ID..."
              className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 dark:bg-gray-800 dark:border-gray-700 dark:text-white outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all shadow-sm"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {/* Stats Summary (UX Boost) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <p className="text-xs font-bold text-gray-400 uppercase">
              Total Deposits
            </p>
            <p className="text-2xl font-bold dark:text-white">{total}</p>
          </div>
        </div>

        {/* Main Table Container */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    OrderId
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Transaction Ref
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">
                    Actions
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    View
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td colSpan="6" className="py-8 px-6">
                        <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded-lg w-full"></div>
                      </td>
                    </tr>
                  ))
                ) : transactions.length > 0 ? (
                  transactions.map((item) => (
                    <tr
                      key={item._id}
                      className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600">
                            {item.user?.image ? (
                              <img
                                src={item.user.image}
                                alt="Transaction Proof"
                                className="rounded-full"
                              />
                            ) : (
                              <FiUser size={20} />
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 dark:text-white">
                              {item.user?.name || "Unknown"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {item.user?.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="font-bold text-gray-900 dark:text-white">
                              {item.orderId || "Unknown"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-2 group">
                          <span className="text-sm font-mono text-gray-600 dark:text-gray-300">
                            {item.hash || item.upi
                              ? `${(item.hash || item.upi).slice(0, 10)}...`
                              : "No Ref"}
                          </span>

                          {(item.hash || item.upi) && (
                            <button
                              onClick={() => copyToClipboard(item.hash)}
                              className="text-gray-400 hover:text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <FiCopy size={14} />
                            </button>
                          )}
                        </div>

                        <p className="text-[10px] text-indigo-500 font-bold mt-1 uppercase tracking-tighter">
                          {item.walletType}
                        </p>
                      </td>
                      <td className="py-5 px-6">
                        <span className="text-lg font-black text-gray-900 dark:text-white">
                          ${item.amount.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-5 px-6">
                        <span
                          className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase border ${getStatusStyle(item.status)}`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="py-5 px-6">
                        {item.status === "process" ? (
                          <div className="flex justify-center items-center min-h-[40px]">
                            {confirmingId === item._id ? (
                              /* --- CONFIRMATION STATE (YES/NO) --- */
                              <div className="flex items-center gap-2 animate-in fade-in zoom-in duration-200">
                                <button
                                  onClick={() => {
                                    handleUpdateStatus(item._id, "approve");
                                    setConfirmingId(null);
                                  }}
                                  className="px-3 py-1.5 bg-emerald-600 text-white text-[10px] font-bold rounded-lg hover:bg-emerald-700 shadow-sm active:scale-90"
                                >
                                  CONFIRM
                                </button>
                                <button
                                  onClick={() => setConfirmingId(null)}
                                  className="p-1.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 rounded-lg hover:bg-gray-200 active:scale-90"
                                >
                                  <FiX size={14} />
                                </button>
                              </div>
                            ) : (
                              /* --- INITIAL ACTION STATE --- */
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setConfirmingId(item._id)} // Toggle confirmation
                                  disabled={isUpdating === item._id}
                                  className="p-2 bg-emerald-500/10 hover:bg-emerald-500 text-emerald-600 hover:text-white rounded-xl transition-all active:scale-95 disabled:opacity-50 group"
                                  title="Approve"
                                >
                                  <FiCheck
                                    size={18}
                                    className="group-hover:scale-110 transition-transform"
                                  />
                                </button>

                                <button
                                  onClick={() => {
                                    setRejectId(item._id);
                                    setShowRejectModal(true);
                                  }}
                                  disabled={isUpdating === item._id}
                                  className="p-2 bg-rose-500/10 hover:bg-rose-600 text-rose-600 hover:text-white rounded-xl transition-all active:scale-95 disabled:opacity-50 group"
                                  title="Reject"
                                >
                                  <FiX
                                    size={18}
                                    className="group-hover:scale-110 transition-transform"
                                  />
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          /* --- STATUS BADGE (Styled version of your text) --- */
                          <div className="flex justify-center">
                            <span
                              className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider
        ${item.status === "completed" || item.status === "approve" ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"}`}
                            >
                              {item.status}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          {/* <FiClock size={14} /> */}
                          {/* {new Date(item.createdAt).toLocaleDateString()} */}
                          {moment(item.createdAt).format(
                            "DD MMM YYYY, hh:mm:ss A",
                          )}
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        <button
                          onClick={() => {
                            setSelectedTransaction(item);
                            setShowViewModal(true);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold rounded-xl transition-all shadow-md shadow-indigo-500/20 active:scale-95"
                        >
                          <FiEye size={16} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-20 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4 text-gray-400">
                          <FiSearch size={24} />
                        </div>
                        <p className="text-gray-500 font-medium">
                          No transactions found matching your criteria.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50/30 dark:bg-gray-800/50 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500 font-medium">
              Page <span className="text-indigo-600">{currentPage}</span> of{" "}
              {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <FiChevronLeft /> Previous
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white flex items-center gap-2 text-sm font-bold hover:bg-indigo-700 shadow-md shadow-indigo-600/20 disabled:opacity-50"
              >
                Next <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-3xl p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Reject Transaction
            </h3>
            <p className="text-gray-500 mb-6">
              Please provide a reason for rejecting this deposit. This will be
              visible to the user.
            </p>
            <textarea
              className="w-full p-4 rounded-2xl border border-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white outline-none focus:ring-4 focus:ring-rose-500/10 focus:border-rose-500 mb-6 h-32 resize-none"
              placeholder="e.g., Invalid UPI Reference, Amount Mismatch..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 py-3 rounded-2xl font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleUpdateStatus(rejectId, "reject", rejectReason)
                }
                disabled={!rejectReason.trim() || isUpdating}
                className="flex-1 py-3 bg-rose-500 hover:bg-rose-600 text-white rounded-2xl font-bold transition-all disabled:opacity-50 shadow-lg shadow-rose-500/20"
              >
                {isUpdating ? "Processing..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showViewModal && selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-opacity">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-[2rem] overflow-hidden shadow-2xl border border-gray-100 dark:border-gray-700">
            {/* Header with Gradient Backdrop */}
            <div className="relative p-6 bg-gradient-to-r from-indigo-500 to-purple-600">
              <h3 className="text-xl font-extrabold text-white flex items-center gap-2">
                <span className="p-2 bg-white/20 rounded-lg">📄</span>
                Transaction Receipt
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-8 space-y-6">
              {/* User Profile Section */}
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700">
                <div className="h-12 w-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 font-bold text-lg">
                  {selectedTransaction?.user?.name?.charAt(0) || "U"}
                </div>
                <div>
                  <p className="text-sm text-gray-400 leading-none mb-1">
                    Customer
                  </p>
                  <p className="font-bold text-gray-800 dark:text-white leading-none">
                    {selectedTransaction?.user?.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {selectedTransaction?.user?.email}
                  </p>
                </div>
              </div>

              {/* Transaction Info Grid */}
              <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                    Amount
                  </p>
                  <p className="text-xl font-black text-gray-800 dark:text-white tracking-tight">
                    ${selectedTransaction?.amount.toLocaleString("en-IN")}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                    Status
                  </p>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide inline-block
              ${
                selectedTransaction.status === "completed"
                  ? "bg-green-100 text-green-600 dark:bg-green-900/30"
                  : selectedTransaction?.status === "pending"
                    ? "bg-amber-100 text-amber-600 dark:bg-amber-900/30"
                    : "bg-rose-100 text-rose-600 dark:bg-rose-900/30"
              }`}
                  >
                    {selectedTransaction?.status}
                  </span>
                </div>

                <div className="col-span-2 space-y-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                    Order Id
                  </p>
                  <p className="font-mono text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded-lg text-indigo-500 break-all border border-gray-100 dark:border-gray-800">
                    {selectedTransaction?.orderId || "N/A"}
                  </p>
                </div>

                <div className="col-span-2 space-y-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                    Wallet Address
                  </p>
                  <p className="font-mono text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded-lg text-indigo-500 break-all border border-gray-100 dark:border-gray-800">
                    {selectedTransaction?.walletAddress || "N/A"}
                  </p>
                </div>
                <div className="col-span-2 space-y-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                    Network
                  </p>
                  <p className="font-mono text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded-lg text-indigo-500 break-all border border-gray-100 dark:border-gray-800">
                    {selectedTransaction?.network || "N/A"}
                  </p>
                </div>

                <div className="col-span-2 space-y-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                    Hash
                  </p>
                  <p className="font-mono text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded-lg text-indigo-500 break-all border border-gray-100 dark:border-gray-800">
                    {selectedTransaction?.hash ||
                      selectedTransaction?.upi ||
                      "N/A"}
                  </p>
                </div>
              </div>

              {/* Rejection Reason (If any) */}
              {selectedTransaction?.rejectReason && (
                <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/30">
                  <p className="text-xs font-bold text-rose-600 uppercase mb-1">
                    Rejection Reason
                  </p>
                  <p className="text-sm text-rose-500 italic">
                    "{selectedTransaction?.rejectReason}"
                  </p>
                </div>
              )}

              {/* Image Section */}
              {selectedTransaction?.image && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">
                    Payment Proof
                  </p>
                  <div
                    onClick={() => setIsImageZoomed(true)}
                    className="group relative rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 dark:border-gray-700 cursor-zoom-in"
                  >
                    <img
                      src={selectedTransaction?.image}
                      alt="Proof"
                      className="w-full h-40 object-cover transform transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-bold">
                        Click to Enlarge
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {selectedTransaction.status === "process" ? (
                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                  {!isConfirmingApprove ? (
                    /* --- INITIAL ACTIONS --- */
                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <button
                          onClick={() => setIsConfirmingApprove(true)} // Show Confirmation
                          disabled={isUpdating === selectedTransaction._id}
                          className="flex-1 flex items-center justify-center gap-2 py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl transition-all shadow-lg shadow-emerald-500/20 font-bold active:scale-95"
                        >
                          <FiCheck size={20} /> Approve
                        </button>

                        <button
                          onClick={() => {
                            setRejectId(selectedTransaction._id);
                            setShowRejectModal(true);
                          }}
                          disabled={isUpdating === selectedTransaction._id}
                          className="flex-1 flex items-center justify-center gap-2 py-4 bg-rose-500 hover:bg-rose-600 text-white rounded-2xl transition-all shadow-lg shadow-rose-500/20 font-bold active:scale-95"
                        >
                          <FiX size={20} /> Reject
                        </button>
                      </div>
                      <button
                        onClick={() => setShowViewModal(false)}
                        className="w-full text-xs text-gray-400 font-bold uppercase tracking-widest hover:text-gray-600 transition-colors"
                      >
                        Close Without Action
                      </button>
                    </div>
                  ) : (
                    /* --- CONFIRMATION STATE --- */
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-900/30 animate-in zoom-in-95 duration-200">
                      <p className="text-emerald-700 dark:text-emerald-400 text-sm font-bold text-center mb-4">
                        Confirm Approval? This cannot be undone.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            handleUpdateStatus(
                              selectedTransaction._id,
                              "approve",
                            );
                            setIsConfirmingApprove(false);
                          }}
                          className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-black text-sm hover:bg-emerald-700 shadow-md transition-all active:scale-95"
                        >
                          YES, APPROVE
                        </button>
                        <button
                          onClick={() => setIsConfirmingApprove(false)}
                          className="flex-1 py-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 rounded-xl font-bold text-sm border border-gray-200 dark:border-gray-600 hover:bg-gray-50 transition-all"
                        >
                          CANCEL
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* --- VIEW ONLY CLOSE BUTTON --- */
                <div className="pt-4">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="w-full py-4 rounded-2xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold text-sm hover:opacity-90 transition-all shadow-lg active:scale-[0.98]"
                  >
                    Close Receipt
                  </button>
                </div>
              )}
            </div>
            {isImageZoomed && (
              <div
                className="absolute inset-0 z-[60] bg-black flex items-center justify-center p-2 animate-in fade-in zoom-in duration-200"
                onClick={() => setIsImageZoomed(false)}
              >
                <button className="absolute top-4 right-4 bg-white/20 p-2 rounded-full text-white hover:bg-white/40">
                  ✕
                </button>
                <img
                  src={selectedTransaction?.image}
                  alt="Full Proof"
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AllInr;
